import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { Comment, SeasonPlan } from '@coin/shared/core/util-models';
import { CommentType, RecordState, SeasonProcessState } from '@coin/shared/core/util-enums';
import { DateHelper, TinyHelpers } from '@coin/shared/core/util-helpers';
import { SeasonRecordsCommentsService } from '@coin/shared/season-mgmt/data-access';
import { UserState } from '@coin/shared/auth/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EditRecordHelper,
  EditRecordRequest,
  SeasonRecordPayoutStateEnum,
  SeasonRecordsUiMappingHelper
} from '@coin/shared/employee-data/util';
import { RecordAchievementDto, SeasonRecord } from '@coin/shared/incentive/util';
import { lastValueFrom } from 'rxjs';
import { IncentiveAchievementsService } from '../../../season-mgmt/services/incentive-achievements.service';
import { ProcessNavigationService } from '../../../season-mgmt/services/process-navigation.service';
import { SeasonState } from '../../../season-mgmt/store';
import { MatInput } from '@angular/material/input';
import { AsyncPipe, PercentPipe, DatePipe } from '@angular/common';
import { V2CardComponent, V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { TwoDecimalPlacesDirective } from '@coin/shared/core/util-directives';
import { CommentsComponent } from '@coin/shared/core/feature-legacy-components';
import { RecordAchievementTableComponent } from '@coin/shared/incentive/feature';

@Component({
  selector: 'admin-app-employee-target-overview',
  templateUrl: './employee-target-overview.component.html',
  styleUrls: ['./employee-target-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    V2CardComponent,
    V2ButtonComponent,
    MatInput,
    TwoDecimalPlacesDirective,
    CommentsComponent,
    RecordAchievementTableComponent,
    AsyncPipe,
    PercentPipe,
    DatePipe,
    TranslatePipe
  ]
})
export class EmployeeTargetOverviewComponent implements OnInit {
  private readonly processNavigationService = inject(ProcessNavigationService);
  private readonly userState = inject(UserState);
  private readonly seasonState = inject(SeasonState);
  private readonly translateService = inject(TranslateService);
  private readonly incentiveAchievement = inject(IncentiveAchievementsService);
  seasonRecordsCommentsService = inject(SeasonRecordsCommentsService);
  dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @Input() record: SeasonRecord;
  @Input() showIncentiveData: boolean;
  readonly editable = input<boolean>(undefined);
  readonly isIncentiveAchievements = input<boolean>(undefined);
  readonly selectedPlan = input<SeasonPlan>(undefined);

  @Output() editItem = new EventEmitter<EditRecordRequest>();

  user$ = this.userState.user$;

  public seasonRecordPayoutStateEnum = SeasonRecordPayoutStateEnum;
  public recordState = RecordState;
  public commentType = CommentType;
  public recordAchievementTable: RecordAchievementDto;
  public recordComments: Comment[] = [];

  public getTranslatedKey(key: string): string {
    return this.translateService.instant(
      `season-mgmt.incentive.monitoring.records.${TinyHelpers.camelCaseToPascalCase(key)}`
    );
  }

  public displayRecordFieldFn(item: string): string {
    return TinyHelpers.pascalcaseToText(SeasonRecordsUiMappingHelper.mapValue(item));
  }

  ngOnInit(): void {
    this.setIncentiveTable();
    this.getRecordComments();
  }

  private getRecordComments(): void {
    this.seasonRecordsCommentsService
      .getComments(this.record?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(comments => {
        this.recordComments = comments.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
  }

  private setIncentiveTable(): void {
    if (!this.isIncentiveAchievements()) {
      return;
    }

    this.incentiveAchievement
      .getRecordAchievements(this.record?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(achievement => {
        this.recordAchievementTable = achievement;
      });
  }

  public editPayoutState(): void {
    this.editItem.emit(EditRecordHelper.payoutState);
  }

  public editPayoutDate(): void {
    this.editItem.emit(EditRecordHelper.payoutDate);
  }

  public update(): void {
    this.setIncentiveTable();
  }

  public async goToPlan(): Promise<void> {
    const season = this.seasonState.currentSeason();
    const planId = this.selectedPlan()?.id ?? this.record?.plan?.id;
    await lastValueFrom(
      this.seasonState.setCurrentPlan({ seasonId: season.id, seasonType: season.seasonType, planId }).pipe(take(1))
    );
    this.processNavigationService.goToStep(SeasonProcessState.PlanDesign);
  }

  public checkDate(date: string): string {
    return date === DateHelper.dbMinDate ? '' : date;
  }

  public editIndividualMultiplierValue(): void {
    this.editItem.emit(EditRecordHelper.individualMultiplierAmount);
  }
}
